// Who writes what a managed workspace's pages show as required
// (02-DESIGN/13-blueprints/02-convergence.md § the console in the child):
// the parent, by its name — *required by Acme* — or *the platform* for a
// blueprint the operator wrote. Read once per page from the tenant's own read,
// which the admin template admits; a member reads nothing and sees no lock.

import { useLoad } from "../../ui/components.tsx";
import { Subjects, type Tenant, type TenantReply } from "../../wire/auth.ts";
import { SERVICE } from "../../wire/identity.ts";
import { useSurface } from "../surface.tsx";
import { childName } from "./relation.ts";

/** Managed is whether a blueprint exists for the workspace, and who wrote it in the person's words. */
export interface Managed {
  managed: boolean;
  /** `Acme`, or `the platform` where the operator wrote it. */
  by: string;
}

/** managedOf reads the relation's facts into the page's words. */
export function managedOf(t: Tenant | undefined): Managed {
  if (t?.managed !== true) {
    return { managed: false, by: "" };
  }
  return { managed: true, by: t.parent !== undefined ? childName(t.parent) : "the platform" };
}

// One read serves every lock a page draws: a Configuration page shows many
// workloads' settings, each asking who requires them.
const held = new WeakMap<object, { at: number; read: Promise<TenantReply> }>();
const FRESH = 30_000;

/** useManaged is the workspace's Managed, or not managed while it is read or where it is refused. */
export function useManaged(): Managed {
  const s = useSurface();
  const [tenant] = useLoad("managed", () => {
    const hit = held.get(s.bus);
    if (hit !== undefined && Date.now() - hit.at < FRESH) {
      return hit.read;
    }
    const read = s.bus.call<TenantReply>(SERVICE, Subjects.tenantGet(), undefined);
    held.set(s.bus, { at: Date.now(), read });
    return read;
  });
  return tenant.state === "ready" ? managedOf(tenant.value.tenant) : { managed: false, by: "" };
}
